import { isClient } from '@/shared/util/ssr';
import React from 'react';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

export function useMeasure<T, E extends HTMLElement = HTMLElement>(
  selector: (rect: Omit<DOMRectReadOnly, 'toJSON'>, element: E | undefined) => T,
  compare: (prev: T, next: T) => boolean = Object.is,
  activeWhenWidthAndHeightEqZero = false,
) {
  const [element, ref] = React.useState<E | null>(null);
  const [rect, setRect] = React.useState<T>(() => {
    return selector(
      {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
      },
      undefined,
    );
  });

  useIsomorphicLayoutEffect(() => {
    let observer: ResizeObserver | undefined = undefined;

    if (element && isClient) {
      observer = new ResizeObserver(([entry]) => {
        const contentRect = entry?.contentRect;
        if (contentRect) {
          if (contentRect.width === 0 && contentRect.height === 0) {
            if (!activeWhenWidthAndHeightEqZero) {
              return;
            }
          }
          const nextRect = selector(contentRect, element);
          setRect((prev) => {
            if (compare(prev, nextRect)) {
              return prev;
            }
            return nextRect;
          });
        }
      });
      observer.observe(element);
    }

    return () => {
      observer?.disconnect();
    };
  }, [element]);

  return [ref, rect, element] as const;
}
